// Tasks: one tap to tick off (with undo), and adding one takes a title and a
// tap on when. Due dates are days, not times: a task is overdue from the day after.
import { Check } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { ApiError, api, consultantsQuery, meQuery } from "../lib/api.ts";
import { dueFrom, dueGroup, dueLabel } from "../lib/due.ts";
import { FilterChips } from "./list.tsx";
import { Alert, Button, Empty, Field, Select, textLinkClass } from "./ui.tsx";

export interface Task {
	id: string;
	title: string;
	notes: string | null;
	due_at: string | null;
	completed_at: string | null;
	/** "booking" for a client's request by link. */
	entity_type: "candidate" | "client" | "booking" | null;
	entity_id: string | null;
	assignee_id: string | null;
	assignee_name: string | null;
	entity_name: string | null;
}

export interface TaskParams {
	scope?: "mine" | "team";
	state?: "open" | "done";
	entityType?: "candidate" | "client";
	entityId?: string;
}

export function tasksQuery(p: TaskParams) {
	const qs = new URLSearchParams(Object.entries(p).filter(([, v]) => v) as [string, string][]);
	return { queryKey: ["tasks", p], queryFn: () => api<Task[]>(`/tasks?${qs}`) };
}

const GROUPS = [
	{ key: "overdue", heading: "Overdue" },
	{ key: "today", heading: "Today" },
	{ key: "later", heading: "Coming up" },
	{ key: "none", heading: "No date" },
] as const;

function TaskRow({
	task,
	showEntity,
	showAssignee,
	onTick,
	busy,
	meId,
	underHeading,
}: {
	task: Task;
	showEntity: boolean;
	showAssignee: boolean;
	onTick: (t: Task) => void;
	busy: boolean;
	meId: string | undefined;
	/** Already under a "Today" heading, so the row needn't say it again. */
	underHeading: boolean;
}) {
	const done = Boolean(task.completed_at);
	const group = dueGroup(task.due_at);
	return (
		<li className="flex items-center gap-3 py-2">
			<button
				type="button"
				onClick={() => onTick(task)}
				disabled={busy}
				aria-label={done ? `Mark not done: ${task.title}` : `Mark done: ${task.title}`}
				className={`inline-flex size-12 shrink-0 items-center justify-center rounded-full border-2 border-b-4 transition-[translate] duration-75 active:translate-y-0.5 motion-reduce:transition-none ${done ? "border-clear bg-clear text-white" : "border-field bg-card text-field hover:border-clear hover:text-clear"}`}
			>
				<Check size={24} weight="bold" aria-hidden="true" />
			</button>
			<div className="min-w-0 flex-1">
				<p className={`font-semibold break-words ${done ? "text-ink-soft line-through" : ""}`}>{task.title}</p>
				<p className="flex flex-wrap gap-x-3 text-sm text-ink-soft">
					{task.due_at && !done && !(underHeading && group === "today") ? (
						<span className={group === "overdue" ? "font-semibold text-stop" : ""}>{dueLabel(task.due_at)}</span>
					) : null}
					{showEntity && task.entity_id && task.entity_name ? (
						<Link
							to={
								task.entity_type === "client"
									? "/clients/$id"
									: task.entity_type === "booking"
										? "/bookings/$id"
										: "/candidates/$id"
							}
							params={{ id: task.entity_id }}
							className={textLinkClass}
						>
							{task.entity_name}
						</Link>
					) : null}
					{showAssignee && task.assignee_name && task.assignee_id !== meId ? (
						<span>For {task.assignee_name}</span>
					) : null}
				</p>
			</div>
		</li>
	);
}

/** A list of tasks with one-tap tick and undo. `grouped` splits open tasks into Overdue, Today, Coming up. */
export function TaskList({
	params,
	showEntity = true,
	showAssignee = false,
	grouped = false,
	max,
	empty,
	emptyHint,
}: {
	params: TaskParams;
	showEntity?: boolean;
	showAssignee?: boolean;
	grouped?: boolean;
	max?: number;
	empty: string;
	/** What to do about it, under the empty line. */
	emptyHint?: string;
}) {
	const queryClient = useQueryClient();
	const q = useQuery(tasksQuery(params));
	const meId = useQuery(meQuery).data?.user.id;
	const [last, setLast] = useState<{ task: Task; done: boolean } | null>(null);
	const tick = useMutation({
		mutationFn: ({ task, done }: { task: Task; done: boolean }) =>
			api(`/tasks/${task.id}`, { method: "PATCH", body: { done } }),
		onMutate: ({ task, done }) => {
			// Move it out of this list straight away; the server catches up.
			queryClient.setQueryData<Task[]>(tasksQuery(params).queryKey, (rows) => rows?.filter((r) => r.id !== task.id));
			setLast({ task, done });
		},
		onSettled: (_d, _e, { task }) => {
			void queryClient.invalidateQueries({ queryKey: ["tasks"] });
			if (task.entity_id) void queryClient.invalidateQueries({ queryKey: [task.entity_type, task.entity_id] });
		},
	});

	const rows = q.data ?? [];
	const shown = max ? rows.slice(0, max) : rows;
	const row = (t: Task) => (
		<TaskRow
			key={t.id}
			task={t}
			showEntity={showEntity}
			showAssignee={showAssignee}
			meId={meId}
			underHeading={grouped && params.state !== "done"}
			busy={tick.isPending}
			onTick={(task) => tick.mutate({ task, done: !task.completed_at })}
		/>
	);

	return (
		<div>
			<div aria-live="polite">
				{last ? (
					<p className="mb-2 flex flex-wrap items-center gap-x-3 rounded-xl bg-sheet px-3 py-1 text-sm">
						<span className="min-w-0 flex-1">
							{last.done ? "Done" : "Back on your list"}: <strong className="font-semibold">{last.task.title}</strong>
						</span>
						<Button
							variant="quiet"
							onClick={() => {
								tick.mutate({ task: last.task, done: !last.done });

								setLast(null);
							}}
						>
							Undo
						</Button>
					</p>
				) : null}
			</div>
			{q.isPending ? <p className="py-2 text-ink-soft">Loading tasks</p> : null}
			{q.isSuccess && rows.length === 0 && empty ? <Empty title={empty}>{emptyHint}</Empty> : null}
			{grouped && params.state !== "done" ? (
				GROUPS.map((g) => {
					const items = shown.filter((t) => dueGroup(t.due_at) === g.key);
					return items.length ? (
						<section key={g.key} className="mt-2 first:mt-0">
							<h3 className={`text-sm font-bold ${g.key === "overdue" ? "text-stop" : "text-ink-soft"}`}>
								{g.heading}
							</h3>
							<ul className="divide-y divide-rule">{items.map(row)}</ul>
						</section>
					) : null;
				})
			) : (
				<ul className="divide-y divide-rule">{shown.map(row)}</ul>
			)}
		</div>
	);
}

const WHEN = [
	{ value: "today", label: "Today" },
	{ value: "tomorrow", label: "Tomorrow" },
	{ value: "week", label: "Next week" },
	{ value: "date", label: "Pick a date" },
	{ value: "none", label: "No date" },
] as const;

export function AddTask({
	entity,
	meId,
	onDone,
}: {
	entity?: { type: "candidate" | "client"; id: string };
	meId: string;
	onDone: () => void;
}) {
	const queryClient = useQueryClient();
	const people = useQuery(consultantsQuery);
	const [when, setWhen] = useState("today");
	const [error, setError] = useState<string | null>(null);
	const save = useMutation({
		mutationFn: (body: unknown) => api("/tasks", { body }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["tasks"] });
			onDone();
		},
		onError: (e) => setError(e instanceof ApiError ? e.message : "The task wasn't saved. Try again."),
	});

	function onSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const form = new FormData(e.currentTarget);
		const title = String(form.get("title") ?? "").trim();
		if (!title) return setError("Say what needs doing.");
		if (when === "date" && !form.get("date")) return setError("Pick a date, or choose another option.");
		setError(null);
		const assigneeId = String(form.get("assignee") ?? "");
		save.mutate({
			title,
			dueAt: dueFrom(when, String(form.get("date") ?? "")),
			...(assigneeId && assigneeId !== meId ? { assigneeId } : {}),
			...(entity ? { entityType: entity.type, entityId: entity.id } : {}),
		});
	}

	return (
		<form onSubmit={onSubmit} noValidate className="mt-3 rounded-xl bg-sheet p-4">
			<Field label="What needs doing?" name="title" placeholder="For example, call back about nights" autoFocus />
			<div className="-mt-4 mb-4">
				<FilterChips name="when" legend="When?" options={WHEN} value={when} onChange={setWhen} />
			</div>
			{when === "date" ? <Field label="Date" name="date" type="date" /> : null}
			{(people.data?.length ?? 0) > 1 ? (
				<Select
					label="For"
					name="assignee"
					defaultValue={meId}
					options={(people.data ?? []).map((p) => ({ value: p.id, label: p.id === meId ? `Me (${p.name})` : p.name }))}
				/>
			) : null}
			{error ? <Alert>{error}</Alert> : null}
			<div className="flex flex-wrap gap-3">
				<Button type="submit" disabled={save.isPending}>
					{save.isPending ? "Saving" : "Add task"}
				</Button>
				<Button variant="quiet" onClick={onDone}>
					Cancel
				</Button>
			</div>
		</form>
	);
}

/** Tasks on one candidate or client: everyone's, so nobody calls the same person twice. */
export function RecordTasks({ entity }: { entity: { type: "candidate" | "client"; id: string } }) {
	const me = useQuery(meQuery).data;
	const [adding, setAdding] = useState(false);
	const params: TaskParams = { entityType: entity.type, entityId: entity.id };
	const q = useQuery(tasksQuery(params));
	if (!me?.permissions.includes("tasks.manage")) return null;
	const none = q.isSuccess && q.data.length === 0;
	return (
		<section aria-labelledby={`tasks-${entity.id}`} className="rounded-[var(--radius-card)] bg-card p-5">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h2 id={`tasks-${entity.id}`} className="font-display text-xl font-bold">
					Tasks
				</h2>
				{!adding ? (
					<Button variant="quiet" onClick={() => setAdding(true)} className="-mr-2">
						Add a task
					</Button>
				) : null}
			</div>
			{adding ? <AddTask entity={entity} meId={me.user.id} onDone={() => setAdding(false)} /> : null}
			{none ? null : (
				<div className="mt-1">
					<TaskList params={params} showEntity={false} showAssignee empty="" />
				</div>
			)}
		</section>
	);
}
